import React, { useState, useEffect, useRef } from 'react';
import {
    Select, MenuItem,
    FormControl, OutlinedInput, InputLabel,
    Checkbox, ListItemText, SelectChangeEvent, TextField, Box
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IOption } from '../types/IOption';


interface SelectDropdownProps {
    label: string;
    options: IOption[];
    value: string | string[];
    multiple?: boolean;
    onChange?: (event: SelectChangeEvent<string | string[]>, child: React.ReactNode) => any;
    renderValue?: (selected: unknown) => string;
    selectAllOption?: boolean;
    noSelectionOption?: boolean,
    required?: boolean,
    isSearchDropdown?: boolean;
}
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const SelectDropdown: React.FC<SelectDropdownProps> = ({
    label,
    options,
    value,
    multiple = false,
    onChange,
    renderValue,
    selectAllOption = false,
    noSelectionOption = false,
    required = false,
    isSearchDropdown = true,
    ...rest
}) => {
    // State to store the current search term entered in the search box
    const [searchTerm, setSearchTerm] = useState("");

    // State to store the options filtered based on the search term
    const [filteredOptions, setFilteredOptions] = useState(options);

    // State to control the visibility of the dropdown menu
    const [menuOpen, setMenuOpen] = useState(false);

    // Ref to keep track of the search input field for managing focus
    const inputRef = useRef<HTMLInputElement>(null);

    // useEffect hook to update filteredOptions whenever searchTerm or options change
    useEffect(() => {
        // Filter the options to include only those that match the search term (case-insensitive)
        setFilteredOptions(
            options.filter((option) =>
                option.label.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [searchTerm, options]);

    const allSelected = Array.isArray(value) ? value.length === options.length : false;

    // Function to open the dropdown menu and focus the search input
    const handleMenuOpen = () => {
        setMenuOpen(true);
        if (inputRef.current) {
            inputRef.current.focus(); // Ensure the search input remains focused
        }
    };

    // Function to close the dropdown menu
    const handleMenuClose = () => {
        setMenuOpen(false);
        setSearchTerm("");
    };

    // Function to handle changes to the selected value
    // const handleChange = (
    //     event: SelectChangeEvent<string | string[]>,
    //     child: React.ReactNode
    // ) => {
    //     if (onChange) {
    //         onChange(event, child); // Call the onChange prop function if provided
    //     }
    //     // Close the menu when a value is selected
    //     setMenuOpen(false);
    // };

    // Function to update the search term state when the search input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    // Function to prevent the default behavior of moving the cursor to a matched dropdown item
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        
        // Prevent the default behavior to stop cursor movement to the matched dropdown item
        e.stopPropagation();
    };

    return (
        <FormControl {...rest} sx={{ width: 550 }}>
            <InputLabel id={`${label}-label`}>{label}</InputLabel>
            <Select
                labelId={`${label}-label`}
                id={`${label}-select`}
                value={value}
                onChange={onChange}
                //onChange={handleChange}
                multiple={multiple}
                input={<OutlinedInput label={label} />}
                renderValue={renderValue}
                MenuProps={MenuProps}
                IconComponent={KeyboardArrowDownIcon}
                required={required}
                open={menuOpen}
                onClose={handleMenuClose}
                onOpen={handleMenuOpen}
            >
                {isSearchDropdown && (
                    <TextField
                        size="small"
                        placeholder="Search..."
                        variant="outlined"
                        fullWidth
                        value={searchTerm}
                        onChange={handleInputChange}
                        inputRef={inputRef}
                        onKeyDown={handleInputKeyDown}
                        onClick={(e) => e.stopPropagation()} // Prevent the dropdown from closing when clicking on the search box
                        autoComplete="off" // Disable autocomplete to remove search history
                        sx={{
                            backgroundColor: "white",
                            position: "sticky",
                            top: 0,
                            zIndex: 999999,
                        }}
                    />
                   
                )}
                {noSelectionOption && ( // Render "No selection" option if specified
                    <MenuItem key="no-selection" value={"All"}>
                        <ListItemText primary="All" />
                    </MenuItem>
                )}
                {selectAllOption && (
                    <MenuItem key="select-all" value="all">
                        <Checkbox checked={allSelected} />
                        <ListItemText primary="Select All" />
                    </MenuItem>
                )}
                {filteredOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {multiple && (
                            <Checkbox checked={value.includes(option.value)} />
                        )}
                        <ListItemText primary={option.label} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default SelectDropdown;
